import {
  resolveAnomalyPolygonColor,
  translateKeyForAnomalyType,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';

const ANOMALY_NAME_TO_CODE: Record<string, number> = {
  FLOODING: 1,
  OVERWATERING: 2,
  DROUGHT: 3,
  SKIPPING_OF_SEEDLINGS: 4,
  PREMATURE_AGING: 5,
  NUTRITIONAL_STRESS: 6,
  COMPLEX_ANOMALY: 7,
};

export interface FieldDetailsRecommendationView {
  codeFromEnum: number;
  typeLabelKey: string;
  /** Full FieldAnomaly dictionary row (holds the localized `recommendation` map). */
  recommendationSource: unknown;
  color: string;
}

function normalizeEnumToken(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .toUpperCase();
}

/** Resolves backend {@code AnomalyType} / {@code codeFromEnum} from an anomaly polygon row. */
export function resolveAnomalyTypeCode(dto: unknown): number | null {
  const rec = dto && typeof dto === 'object' ? (dto as Record<string, unknown>) : {};
  const nested = rec['anomalyPolygon'];
  const nestedRec =
    nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
  const typeRaw =
    rec['anomalyType'] ??
    rec['type'] ??
    nestedRec?.['anomalyType'] ??
    nestedRec?.['type'];

  if (typeof typeRaw === 'number' && Number.isFinite(typeRaw)) {
    return typeRaw;
  }

  const n = Number(typeRaw);
  if (Number.isFinite(n) && n > 0) {
    return n;
  }

  if (typeof typeRaw === 'string' && typeRaw.trim()) {
    const token = normalizeEnumToken(typeRaw);
    const byName = ANOMALY_NAME_TO_CODE[token];
    if (byName != null) {
      return byName;
    }
  }

  return null;
}

/** Extracts distinct anomaly type codes from a list of anomaly polygon rows. */
export function extractAnomalyCodesFromRows(rows: unknown[]): number[] {
  const codes = new Set<number>();
  for (const row of rows || []) {
    const code = resolveAnomalyTypeCode(row);
    if (code != null) {
      codes.add(code);
    }
  }
  return [...codes];
}

export function fieldAnomalyRowsToCodeMap(rows: unknown[]): Map<number, unknown> {
  const map = new Map<number, unknown>();
  for (const row of rows || []) {
    const code = Number((row as {codeFromEnum?: unknown})?.codeFromEnum);
    if (Number.isFinite(code)) {
      map.set(code, row);
    }
  }
  return map;
}

/** One recommendation card per distinct anomaly type on the selected day. */
export function buildRecommendationsForAnomalyRows(
  rows: unknown[],
  fieldAnomalyByCode: Map<number, unknown>,
): FieldDetailsRecommendationView[] {
  const seen = new Set<number>();
  const out: FieldDetailsRecommendationView[] = [];

  for (const row of rows || []) {
    const code = resolveAnomalyTypeCode(row);
    if (code == null || seen.has(code)) {
      continue;
    }
    seen.add(code);
    const dict = fieldAnomalyByCode.get(code) ?? null;
    out.push({
      codeFromEnum: code,
      typeLabelKey: translateKeyForAnomalyType(code) || '',
      recommendationSource: dict,
      color: resolveAnomalyPolygonColor(row),
    });
  }

  return out;
}
